import { isNativeWebView } from "@/hooks/useNativeBridge";

export type SafeAreaInsets = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

declare global {
  interface WindowEventMap {
    nativeSafeAreaChanged: CustomEvent<SafeAreaInsets>;
  }
}

const STORAGE_KEY = "native_safe_area_insets";

function isValidInsets(value: unknown): value is SafeAreaInsets {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return typeof obj.top === "number" && typeof obj.bottom === "number" && typeof obj.left === "number" && typeof obj.right === "number";
}

function loadCachedInsets(): SafeAreaInsets | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    return isValidInsets(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function saveCachedInsets(insets: SafeAreaInsets): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(insets));
  } catch {
    // ignore
  }
}

function applyCssVariables(insets: SafeAreaInsets) {
  const root = document.documentElement;
  root.style.setProperty("--safe-area-inset-top", `${insets.top}px`);
  root.style.setProperty("--safe-area-inset-bottom", `${insets.bottom}px`);
  root.style.setProperty("--safe-area-inset-left", `${insets.left}px`);
  root.style.setProperty("--safe-area-inset-right", `${insets.right}px`);
}

function handleNativeEvent(event: CustomEvent<SafeAreaInsets>) {
  const raw = event.detail;
  if (!isValidInsets(raw)) return;
  const insets: SafeAreaInsets = {
    top: raw.top,
    bottom: raw.bottom,
    left: raw.left,
    right: raw.right,
  };
  saveCachedInsets(insets);
  applyCssVariables(insets);
}

// Only initialize safe area handling in native webview
if (isNativeWebView()) {
  const cached = loadCachedInsets();
  if (cached) {
    applyCssVariables(cached);
  }

  window.addEventListener("nativeSafeAreaChanged", handleNativeEvent);
}
